/**
 * LevelUpOverlay — full-screen level-up celebration.
 *
 * iOS source: LevelUpScreen.tsx + ConfettiBurst.tsx + Sparkle.tsx +
 * SkyBackground.tsx.
 *   - Full-screen vertical gradient #032B54 → #0757A0 → #1A7BD4 → #4DA8ED → #8CCAF5,
 *     with the SkyBackground (spinning sun + clouds) layered over it.
 *   - Confetti burst (reuses the shared @/ui Confetti) + four timed Sparkles.
 *   - Sequenced center content (iOS BASE delay 500ms + offsets 200/500/800/1800):
 *       "Congrats, {firstName}!" → "Level {n}" → parrot → level NAME +
 *       "Keep learning to reach {nextLevelName}!" → "Continue".
 *   - Continue is a blue full-width Button pinned to the bottom safe area.
 *   - Haptics fire on iOS — omitted on web (no equivalent).
 *
 * The "Level N" headline uses clamp() so it never overflows narrow phones.
 */
import { FC, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { colors, tokens } from "@/ui";
import Confetti from "@/ui/Confetti";
import { callWithAuth, getUserInfo } from "@/api/Api";
import { PARROT_LEVELS } from "@/data/parrotLevels";
import SkyBackground from "@/components/Home/SkyBackground";
import { markLevelAnimationSeen } from "./useLevelUp";
import parrot from "../../assets/parrot-bird.png";

export interface LevelUpOverlayProps {
  firstName: string;
  /** The level reached. */
  level: number;
  onContinue: () => void;
}

// Animation beats (ms) mirror LevelUpScreen BASE delay 500 + offsets
// (200 heading / 500 parrot / 800 name / 1800 button).
const BASE = 500;
const BEAT_HEADING = BASE + 200;
const BEAT_PARROT = BASE + 500;
const BEAT_NAME = BASE + 800;
const BEAT_BUTTON = BASE + 1800;

// iOS Sparkle positions (LevelUpScreen.tsx:135-138), as viewport fractions, with
// their per-sparkle delay (the shared BASE 500ms is added inside the keyframe).
const SPARKLES: { left: string; top: string; delay: number }[] = [
  { left: "15%", top: "22%", delay: 300 },
  { left: "75%", top: "18%", delay: 800 },
  { left: "55%", top: "28%", delay: 1200 },
  { left: "25%", top: "32%", delay: 500 },
];

const LevelUpOverlay: FC<LevelUpOverlayProps> = ({
  firstName,
  level,
  onContinue,
}) => {
  // iOS LevelUpScreen.tsx:51 — `user?.firstName || "Learner"`.
  const displayName = firstName || "Learner";
  const lvl = PARROT_LEVELS[level - 1];
  // Fall back to the last level at max so the "Keep learning to reach…" line
  // always renders (iOS LevelUpScreen.tsx:50).
  const next = PARROT_LEVELS[level] ?? PARROT_LEVELS[PARROT_LEVELS.length - 1];
  const [stage, setStage] = useState(0);

  // Namespace persistence by user id (useLevelUp parity) so an account switch
  // in the same browser can't replay a celebration against the wrong key.
  const userInfoQuery = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });
  const userId = userInfoQuery.data?.id;

  useEffect(() => {
    // The overlay has actually rendered, so the user is seeing (or about to
    // see) this level's animation. Persist it now — not on Continue — so a
    // navigation away (e.g. the /level-up route) can never replay it.
    // Mirrors iOS LevelUpScreen.tsx markLevelAnimationSeen-on-mount.
    markLevelAnimationSeen(level, userId);
  }, [level, userId]);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), BEAT_HEADING),
      setTimeout(() => setStage(2), BEAT_PARROT),
      setTimeout(() => setStage(3), BEAT_NAME),
      setTimeout(() => setStage(4), BEAT_BUTTON),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const fade = (visible: boolean, dy = 12): React.CSSProperties => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : `translateY(${dy}px)`,
    transition: "opacity 400ms ease-out, transform 400ms ease-out",
  });

  const sparkleKeyframes = useMemo(
    () =>
      `@keyframes levelup-sparkle { 0%,100% { opacity: 0; transform: scale(0.6); } 50% { opacity: 1; transform: scale(1.2); } }`,
    [],
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: tokens.zIndex.modal,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 24,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <style>{sparkleKeyframes}</style>

      {/* Use the SAME background as Home/Learn (gradient that fades to white at
          the bottom + the spinning sun + scattered clouds anchored to the
          centered phone-width column). Reused verbatim so the level-up sky reads
          identically to the home screen on every viewport. */}
      <SkyBackground />

      {SPARKLES.map((s, i) => (
        <span
          key={i}
          aria-hidden
          style={{
            position: "absolute",
            left: s.left,
            top: s.top,
            zIndex: 1,
            color: colors.white,
            fontSize: "var(--base-font-size)",
            animation: `levelup-sparkle 800ms ease-in-out ${BASE + s.delay}ms infinite`,
          }}
        >
          ✦
        </span>
      ))}

      <Confetti active />

      <div
        style={{
          flex: 1,
          width: "100%",
          maxWidth: 480,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 12,
          zIndex: 2,
        }}
      >
        {/* Congrats */}
        <span
          style={{
            fontSize: "var(--base-font-size-2xlarge)",
            fontWeight: 600,
            color: colors.white,
            opacity: stage >= 1 ? 0.85 : 0,
            transition: "opacity 400ms ease-out",
          }}
        >
          Congrats, {displayName}!
        </span>

        {/* Level N */}
        <span
          style={{
            fontSize: "clamp(48px, 18vw, 84px)",
            fontWeight: 800,
            letterSpacing: -1,
            color: colors.white,
            lineHeight: 1,
            textShadow: "0 4px 16px rgba(0,0,0,0.25)",
            ...fade(stage >= 1, 16),
          }}
        >
          Level {level}
        </span>

        {/* Parrot */}
        <img
          src={parrot}
          alt=""
          aria-hidden
          style={{
            width: 160,
            height: 160,
            objectFit: "contain",
            opacity: stage >= 2 ? 1 : 0,
            transform: stage >= 2 ? "scale(1)" : "scale(0.6)",
            transition: "opacity 400ms ease-out, transform 500ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        />

        {/* Name + next — dark text: these sit on the lighter lower part of the
            Home/Learn gradient (fading to white), so near-black reads cleanly
            (same dark-on-light treatment Home uses), unlike the white Congrats /
            Level headings up top on the dark-blue sky. */}
        <div style={fade(stage >= 3)}>
          <div
            style={{
              fontSize: "var(--base-font-size-2xlarge)",
              fontWeight: 700,
              color: "#1C1C1E",
            }}
          >
            {lvl?.name ?? `Level ${level}`}
          </div>
          {next && (
            <div
              style={{
                fontSize: "var(--base-font-size)",
                color: "#1C1C1E",
                opacity: 0.65,
                marginTop: 6,
              }}
            >
              Keep learning to reach {next.name}!
            </div>
          )}
        </div>
      </div>

      {/* Continue — blue full-width button pinned to the bottom safe area
          (iOS LevelUpScreen.tsx:166-170). */}
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          paddingBottom: "max(20px, env(safe-area-inset-bottom))",
          zIndex: 2,
          ...fade(stage >= 4, 20),
        }}
      >
        <button
          type="button"
          onClick={onContinue}
          style={{
            width: "100%",
            height: 56,
            borderRadius: 28,
            background: colors.blue,
            color: colors.white,
            fontSize: "var(--base-font-size-large)",
            fontWeight: 700,
            border: "none",
            cursor: "pointer",
            // While faded in (stage < 4) the button is opacity:0 but still in the
            // layout — without this an early tap in its region dismisses the
            // celebration. Disable hit-testing until it's actually visible.
            pointerEvents: stage >= 4 ? "auto" : "none",
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default LevelUpOverlay;
